import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ClipboardCheck, Save, Send, SearchX } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { useHasPlanFeature } from "../../../contexts/PlanContext";
import { fromDateTimeLocal, toDateTimeLocal } from "../../../utils/datetime";
import {
  AppButton, Callout, ConfirmModal, EmptyState, FormActionBar, PageHeader, StepRail, cn,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import FormatStep from "./FormatStep";
import DetailsStep from "./DetailsStep";
import QuestionsStep from "./QuestionsStep";
import RulesStep from "./RulesStep";
import AudienceStep from "./AudienceStep";
import ReviewStep from "./ReviewStep";
import PaperSummary from "./PaperSummary";
import UsageChoice from "./UsageChoice";
import useBuilderData from "./useBuilderData";
import { assessmentBuilderTrail, parentOf } from "../../QuizBank/quizCenterTrail";
import {
  applyTemplate, applyType, applyUsage, blankForm, fromAssessment, isPublicOnly, publishProblems,
  publishWarnings, stepProblems, stepsFor, templatesFor, toPayload,
} from "./builderRules";

/** What each step asks of the author, in a sentence. */
const STEP_COPY = {
  format: {
    title: "Choose a format",
    intro: "Pick the closest match. It sets up the sections, the timer and the rules for you — you can rename, add or remove sections and change every rule afterwards.",
  },
  details: { title: "Name and instructions", intro: "What it is called, what kind it is, and what students read before they start." },
  questions: { title: "Add the questions", intro: "Fill each section from your question bank. Every change is kept until you save." },
  rules: { title: "Set the rules", intro: "The timer, the marking, what students see afterwards and how the exam is guarded." },
  audience: { title: "Choose who takes it", intro: "Pick the students and, if you like, when it opens and closes." },
  review: { title: "Review and publish", intro: "Check everything once. Publishing gives it to students and tells them." },
};
const LIBRARY_AUDIENCE = { title: "Set the price", intro: "Free, or paid on the marketplace." };
/** The same steps, worded for a paper that is run through public exam links. */
const PUBLIC_STEP_COPY = {
  details: { title: "Name and instructions", intro: "What it is called and what candidates read before they start." },
  rules: {
    title: "Time and marks",
    intro: "How long it lasts and how it is marked. Everything else — who can enter, security and results — is set on the public exam.",
  },
  review: {
    title: "Review and make it ready",
    intro: "Check everything once. Saving makes it ready for a public exam — your students are not told.",
  },
};

const hasContent = (form) => form.sections.some(
  (s) => (s.questions || []).length + (s.rules || []).length + (s.coding || []).length > 0
);

/**
 * AssessmentBuilderPage — create or edit an assessment, one step at a time.
 * `/assessments/create` · `/assessments/edit/:id`, and the platform library's
 * `/sa/marketplace/assessments/create|edit/:id` (pricing instead of students).
 *
 * A paper can be for the institute's students or only for public exam links
 * (`assignmentType: "public"`, chosen first). A public paper has no Students
 * step and is published without telling anyone — public exams need a
 * published paper — and publishing it goes straight on to the new public exam.
 * `/assessments/create?for=public-exam` opens with that choice made.
 *
 * Every rule is in builderRules.js; this page holds the form, walks the steps
 * and talks to the API. A step's problems are shown once the author tries to
 * move on, and disappear as each is fixed.
 */
export default function AssessmentBuilderPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const isLibrary = useLocation().pathname.startsWith("/sa/");
  const [searchParams] = useSearchParams();
  const startPublic = !isEdit && !isLibrary && searchParams.get("for") === "public-exam";
  const hasPlanFeature = useHasPlanFeature();
  const base = isLibrary ? "/sa/marketplace/assessments" : "/assessments";
  const data = useBuilderData({ isLibrary });

  const [form, setFormState] = useState(() => (startPublic ? applyUsage(blankForm(), "public") : blankForm()));
  const isPublic = !isLibrary && isPublicOnly(form);
  const steps = useMemo(() => stepsFor({ isEdit, isLibrary, isPublic }), [isEdit, isLibrary, isPublic]);
  const [loadState, setLoadState] = useState(isEdit ? "loading" : "ready");
  const [meta, setMeta] = useState({ status: "draft", totalAttempts: 0 });
  const [stepIndex, setStep] = useState(isEdit ? steps.length - 1 : 0);
  const [reached, setReached] = useState(isEdit ? steps.length - 1 : 0);
  // Choosing public takes the Students step out, so a remembered index can
  // point past the end; it then means the last step.
  const step = Math.min(stepIndex, steps.length - 1);
  const [attempted, setAttempted] = useState({});
  const [saving, setSaving] = useState(null);
  const [failure, setFailure] = useState("");
  const [dirty, setDirty] = useState(false);
  const [studentLabels, setStudentLabels] = useState({});
  const [pendingTemplate, setPendingTemplate] = useState(null);

  const setForm = useCallback((updater) => {
    setFormState(updater);
    setDirty(true);
  }, []);
  const set = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), [setForm]);

  // ── load ───────────────────────────────────────────────────────────
  const load = useCallback(() => {
    if (!isEdit) return;
    setLoadState("loading");
    apiClient.get(`/assessments/${id}`)
      .then(({ data: res }) => {
        const a = res.assessment;
        setFormState(fromAssessment(a, { toDateTimeLocal }));
        // An edit opens on Review — the last of THIS paper's steps.
        const last = stepsFor({ isEdit, isLibrary, isPublic: !isLibrary && a.assignmentType === "public" }).length - 1;
        setStep(last);
        setReached(last);
        setMeta({ status: a.status || "draft", totalAttempts: a.totalAttempts || 0 });
        const labels = {};
        for (const s of a.assignedStudents || []) {
          if (s && s._id) labels[s._id] = s.name || s.email || "Student";
        }
        setStudentLabels(labels);
        setDirty(false);
        setLoadState("ready");
      })
      .catch((err) => setLoadState(err?.response?.status === 404 ? "missing" : "error"));
  }, [id, isEdit, isLibrary]);

  useEffect(() => { load(); }, [load]);

  // Leaving with unsaved work asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const stepKey = steps[step]?.key;
  const { codingKnown } = data;
  const { allowed: codingAllowed, shown: codingShown } = data.coding;
  const ctx = useMemo(
    () => ({ codingAllowed: codingKnown ? codingAllowed : undefined, isLibrary }),
    [codingKnown, codingAllowed, isLibrary],
  );
  // Where Coding Practice is off, the formats carry no coding section.
  const templates = useMemo(() => templatesFor({ coding: codingShown }), [codingShown]);
  const locked = isEdit && meta.status === "published" && meta.totalAttempts > 0;

  const formatProblem = stepKey === "format" && !form.template
    ? [{ message: "Choose a format to continue — or Start from scratch." }]
    : [];
  const liveProblems = stepKey === "format" ? formatProblem : stepProblems(form, stepKey, ctx);
  const shownProblems = attempted[stepKey] ? liveProblems : [];
  const errors = {};
  for (const p of shownProblems) if (p.field && !errors[p.field]) errors[p.field] = p.message;

  const allPublishProblems = useMemo(() => (locked ? [] : publishProblems(form, ctx)), [form, ctx, locked]);
  const warnings = useMemo(() => publishWarnings(form), [form]);

  // Steps behind the author that still need something.
  const flagged = steps
    .filter((s, i) => i < reached && (
      s.key === "audience"
        ? allPublishProblems.some((p) => p.step === "audience")
        : s.key !== "format" && s.key !== "review" && stepProblems(form, s.key, ctx).length > 0
    ))
    .map((s) => s.key);

  const goTo = (index) => {
    const next = Math.max(0, Math.min(index, steps.length - 1));
    setStep(next);
    setReached((r) => Math.max(r, next));
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };
  const goToKey = (key) => {
    const i = steps.findIndex((s) => s.key === key);
    if (i >= 0) goTo(i);
  };

  const next = () => {
    setAttempted((a) => ({ ...a, [stepKey]: true }));
    if (liveProblems.length) {
      setTimeout(() => document.querySelector('[aria-invalid="true"]')?.focus?.(), 0);
      return;
    }
    goTo(step + 1);
  };

  // ── format ─────────────────────────────────────────────────────────
  const chooseTemplate = (key) => {
    if (key === form.template) return;
    if (hasContent(form)) setPendingTemplate(key);
    else setForm((f) => applyTemplate(f, key, { coding: codingShown }));
  };

  // ── save ───────────────────────────────────────────────────────────
  const save = async (publish) => {
    setFailure("");
    if (!form.title.trim()) {
      setAttempted((a) => ({ ...a, details: true }));
      goToKey("details");
      return;
    }
    if (publish && allPublishProblems.length) {
      setAttempted({ details: true, questions: true, rules: true, audience: true });
      goToKey("review");
      return;
    }
    setSaving(publish ? "publish" : "draft");
    try {
      const payload = toPayload(form, { isLibrary, fromDateTimeLocal });
      let assessmentId = id;
      if (isEdit) {
        await apiClient.put(`/assessments/${id}`, payload);
      } else {
        const { data: res } = await apiClient.post("/assessments", payload);
        assessmentId = res.assessment?._id;
      }
      if (publish && meta.status !== "published" && assessmentId) {
        await apiClient.put(`/assessments/${assessmentId}/publish`);
      }
      setDirty(false);
      const firstPublish = publish && meta.status !== "published";
      // A paper made for public exams goes straight on to its public exam.
      if (firstPublish && isPublic && assessmentId && hasPlanFeature("publicExams")) {
        navigate(`/public-exams/new?assessment=${assessmentId}`, { state: { readyAssessment: form.title.trim() } });
        return;
      }
      const notice = firstPublish
        ? (isPublic ? "Ready. You can use it in a public exam now." : "Published. Students can see it now.")
        : isEdit ? "Changes saved." : "Saved as a draft. Publish it when you are ready.";
      if (isLibrary || !assessmentId) navigate(base, { state: { notice } });
      else navigate(`/assessments/${assessmentId}`, { replace: true, state: { notice } });
    } catch (err) {
      setFailure(err?.response?.data?.message || "The assessment could not be saved. Check your connection and try again.");
      window.scrollTo?.({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(null);
    }
  };

  // Back and Cancel go up the hierarchy: an edit to the assessment's page, a new
  // one to the list.
  const trail = assessmentBuilderTrail({ isLibrary, id, title: form.title });
  const goBack = () => navigate(parentOf(trail));

  // ── render ─────────────────────────────────────────────────────────
  const header = (
    <PageHeader
      icon={ClipboardCheck}
      title={isEdit ? "Edit assessment" : "New assessment"}
      description={isEdit
        ? form.title
        : isPublic
          ? "A few guided steps. It is used only through public exam links — your students never see it."
          : "A few guided steps. Nothing reaches students until you publish."}
      breadcrumbs={trail}
    />
  );

  if (loadState === "loading") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading assessment">
        {header}
        <div className="tw-grid tw-gap-6 min-[992px]:tw-grid-cols-[15rem_minmax(0,1fr)]">
          <Shimmer height={320} />
          <Shimmer height={480} />
        </div>
      </div>
    );
  }
  if (loadState === "missing" || loadState === "error") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header}
        {loadState === "missing" ? (
          <EmptyState
            icon={SearchX}
            title="Assessment not found"
            description="It may have been deleted."
            action={<AppButton variant="outline" onClick={() => navigate(base)}>Back to assessments</AppButton>}
          />
        ) : (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
            The assessment could not be loaded.
          </Callout>
        )}
      </div>
    );
  }

  const copy = stepKey === "audience" && isLibrary
    ? LIBRARY_AUDIENCE
    : (isPublic && PUBLIC_STEP_COPY[stepKey]) || STEP_COPY[stepKey];
  const isReview = stepKey === "review";
  const showSummary = ["questions", "rules", "audience"].includes(stepKey);
  const busy = Boolean(saving);
  const published = meta.status === "published";
  const codingBankPath = isLibrary ? "/sa/coding-questions" : "/questions-management";
  // Only where public exams are part of the plan — or the paper already is one,
  // so it can be given back to students.
  const usageChoice = !isLibrary && (isPublic || hasPlanFeature("publicExams")) ? (
    <UsageChoice
      value={isPublic ? "public" : "students"}
      onChange={(usage) => setForm((f) => applyUsage(f, usage))}
      disabled={locked}
    />
  ) : null;

  const stepBody = {
    format: (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {usageChoice}
        <FormatStep value={form.template} onChange={chooseTemplate} templates={templates} loading={!codingKnown} />
      </div>
    ),
    details: (
      <DetailsStep
        form={form}
        set={set}
        setForm={setForm}
        onTypeChange={(type) => setForm((f) => applyType(f, type))}
        errors={errors}
        locked={locked}
        codingShown={codingShown}
        usageChoice={isEdit ? usageChoice : null}
      />
    ),
    questions: (
      <QuestionsStep
        form={form}
        setForm={setForm}
        categories={data.categories}
        coding={data.coding}
        problems={shownProblems}
        locked={locked}
        codingBankPath={codingBankPath}
      />
    ),
    rules: <RulesStep form={form} set={set} setForm={setForm} errors={errors} locked={locked} codingShown={codingShown} />,
    audience: (
      <AudienceStep
        form={form}
        set={set}
        setForm={setForm}
        batches={data.batches}
        courses={data.courses}
        studentLabels={studentLabels}
        onStudentLabel={(sid, name) => setStudentLabels((m) => ({ ...m, [sid]: name }))}
        isLibrary={isLibrary}
        locked={locked}
        errors={{}}
      />
    ),
    review: (
      <ReviewStep
        form={form}
        problems={allPublishProblems}
        warnings={warnings}
        onGoTo={goToKey}
        isLibrary={isLibrary}
        batches={data.batches}
        courses={data.courses}
        studentLabels={studentLabels}
        status={meta.status}
        onGoToUsage={isEdit ? "details" : "format"}
      />
    ),
  }[stepKey];

  const generalProblems = shownProblems.filter((p) => !p.field && !p.section && stepKey !== "questions");

  return (
    <div className="has-form-actionbar tw-flex tw-flex-col tw-gap-4">
      {header}

      {failure && <Callout tone="danger" role="alert">{failure}</Callout>}
      {locked && (
        <Callout tone="warning">
          Students have already taken this assessment, so its questions and rules are locked — that keeps every
          result comparable. You can still change the description, the instructions, the closing time and the
          number of attempts. To change more, duplicate it.
        </Callout>
      )}

      <div
        className={cn(
          "tw-grid tw-items-start tw-gap-6 min-[992px]:tw-grid-cols-[15rem_minmax(0,1fr)]",
          showSummary && "min-[1200px]:tw-grid-cols-[15rem_minmax(0,1fr)_17rem]",
        )}
      >
        <div className="min-[992px]:tw-sticky min-[992px]:tw-top-4">
          <StepRail steps={steps} current={step} reached={reached} onSelect={goTo} flagged={flagged} />
        </div>

        <section
          aria-labelledby="builder-step-title"
          className={cn("tw-flex tw-min-w-0 tw-flex-col tw-gap-4", !showSummary && "tw-max-w-4xl")}
        >
          <div className="tw-flex tw-flex-col tw-gap-1">
            <h2 id="builder-step-title" className="tw-m-0 tw-text-[1.375rem] tw-font-semibold tw-tracking-tight tw-text-content">
              {copy?.title}
            </h2>
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">{copy?.intro}</p>
          </div>
          {generalProblems.map((p) => (
            <Callout key={p.message} tone="danger" role="alert">{p.message}</Callout>
          ))}
          {stepBody}
        </section>

        {showSummary && (
          <div className="tw-hidden min-[1200px]:tw-sticky min-[1200px]:tw-top-4 min-[1200px]:tw-block">
            <PaperSummary form={form} />
          </div>
        )}
      </div>

      <FormActionBar
        info={dirty ? <span role="status">Unsaved changes</span> : null}
      >
        {step === 0 ? (
          <AppButton type="button" variant="outline" onClick={goBack} disabled={busy}>Cancel</AppButton>
        ) : (
          <AppButton type="button" variant="outline" onClick={() => goTo(step - 1)} disabled={busy}>
            <ArrowLeft size={16} aria-hidden="true" /> Back
          </AppButton>
        )}
        {(stepKey !== "format" || isEdit) && !published && (
          <AppButton type="button" variant="outline" onClick={() => save(false)} disabled={busy || !form.title.trim()}>
            <Save size={16} aria-hidden="true" /> {saving === "draft" ? "Saving…" : "Save draft"}
          </AppButton>
        )}
        {isReview ? (
          <AppButton type="button" onClick={() => save(true)} disabled={busy || (!published && allPublishProblems.length > 0)}>
            <Send size={16} aria-hidden="true" />
            {saving === "publish" ? "Saving…" : published ? "Save changes" : isPublic ? "Save and set up public exam" : "Publish"}
          </AppButton>
        ) : (
          <>
            {published && (
              <AppButton type="button" variant="outline" onClick={() => save(true)} disabled={busy}>
                <Save size={16} aria-hidden="true" /> {saving ? "Saving…" : "Save changes"}
              </AppButton>
            )}
            <AppButton type="button" onClick={next} disabled={busy}>
              Continue <ArrowRight size={16} aria-hidden="true" />
            </AppButton>
          </>
        )}
      </FormActionBar>

      <ConfirmModal
        show={!!pendingTemplate}
        onHide={() => setPendingTemplate(null)}
        onConfirm={() => { setForm((f) => applyTemplate(f, pendingTemplate, { coding: codingShown })); setPendingTemplate(null); }}
        title="Switch to this format?"
        message="Its sections replace the ones you have, and the questions you added are taken off. Your name, description and students stay."
        confirmText="Switch format"
        variant="warning"
      />
    </div>
  );
}
